import { MANUAL_PAYMENT_METHODS, upiLink, upiReference } from "@luckymenu/billing-core";
import { parseRupees } from "@luckymenu/core";

/**
 * Taking payment (OP-12): cash with change, UPI to the restaurant's own ID,
 * card, or anything else with a note; a split is one payment per part. The
 * amount is what the bill takes: for cash, what the diner hands over only
 * decides the change. LuckyMenu records what was received (BL-10).
 */

export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];
export const PAYMENT_METHODS = MANUAL_PAYMENT_METHODS;

/** What staff type on the payment screen. Empty `amount` is the whole balance. */
export type PaymentDraft = {
	method: ManualPaymentMethod;
	/** Rupees for this payment; empty for all that is left. */
	amount: string;
	/** Cash only: what the diner handed over; empty for exactly the amount. */
	received: string;
	/** UPI or card: a transaction or slip number, if noted. */
	reference: string;
	/** Required for `other`, such as a credit customer's name. */
	note: string;
};

export const EMPTY_PAYMENT_DRAFT: PaymentDraft = {
	method: "cash",
	amount: "",
	received: "",
	reference: "",
	note: "",
};

export type PaymentProblem = "invalid" | "moreThanBalance" | "notEnough" | "required";

export type PaymentPart = {
	method: ManualPaymentMethod;
	amountPaise: number;
	reference: string | null;
	note: string | null;
};

export type PaymentDraftResult =
	| { ok: true; payment: PaymentPart; changePaise: number }
	| {
			ok: false;
			problems: { amount?: PaymentProblem; received?: PaymentProblem; note?: PaymentProblem };
	  };

/** The amount this payment takes: the typed one, or the whole balance. Null when not a valid amount. */
export function paymentAmount(draft: Pick<PaymentDraft, "amount">, balancePaise: number) {
	if (draft.amount.trim() === "") return balancePaise;
	return parseRupees(draft.amount);
}

/**
 * The payment to record, and the change to give back for cash, or what is
 * wrong. More than the balance is refused (the server would refuse it too);
 * cash received below the amount is not enough.
 */
export function paymentFromDraft(draft: PaymentDraft, balancePaise: number): PaymentDraftResult {
	const problems: { amount?: PaymentProblem; received?: PaymentProblem; note?: PaymentProblem } =
		{};
	const amount = paymentAmount(draft, balancePaise);
	if (amount === null || amount <= 0) problems.amount = "invalid";
	else if (amount > balancePaise) problems.amount = "moreThanBalance";
	let changePaise = 0;
	if (draft.method === "cash" && amount !== null && draft.received.trim() !== "") {
		const received = parseRupees(draft.received);
		if (received === null) problems.received = "invalid";
		else if (received < amount) problems.received = "notEnough";
		else changePaise = received - amount;
	}
	const note = draft.note.trim();
	if (draft.method === "other" && note === "") problems.note = "required";
	if (amount === null || Object.keys(problems).length > 0) return { ok: false, problems };
	const reference = draft.reference.trim();
	return {
		ok: true,
		changePaise,
		payment: {
			method: draft.method,
			amountPaise: amount,
			reference:
				(draft.method === "upi" || draft.method === "card") && reference ? reference : null,
			note: note || null,
		},
	};
}

/** Change for cash received against an amount; null while the cash does not cover it. */
export function changeFor(receivedPaise: number, amountPaise: number): number | null {
	return receivedPaise >= amountPaise ? receivedPaise - amountPaise : null;
}

const NOTES = [10_000, 20_000, 50_000, 200_000];

/**
 * Cash a diner is likely to hand over for `amountPaise`: the next ₹100,
 * ₹200, ₹500 and ₹2000 above it, the lowest three, for one-tap entry (the
 * exact amount is what an empty field means).
 */
export function cashSuggestions(amountPaise: number): number[] {
	if (amountPaise <= 0) return [];
	const out: number[] = [];
	for (const note of NOTES) {
		// The next multiple above: ₹540 → ₹600, ₹1,000, ₹2,000; ₹500 → ₹600, ₹1,000, ₹2,000.
		const next = (Math.floor(amountPaise / note) + 1) * note;
		if (!out.includes(next)) out.push(next);
	}
	return out.sort((a, b) => a - b).slice(0, 3);
}

/**
 * The UPI link for one payment, for the full-screen QR (P4-T18): to the
 * restaurant's UPI ID now, for this part's amount, with the bill number as
 * the note and the bill's short reference. Null without a UPI ID.
 */
export function paymentUpiLink(options: {
	vpa: string | null;
	payeeName: string;
	amountPaise: number;
	billNo: string;
	billId: string;
}): string | null {
	if (!options.vpa || options.amountPaise <= 0) return null;
	try {
		return upiLink({
			vpa: options.vpa,
			payeeName: options.payeeName,
			amountPaise: options.amountPaise,
			billNo: options.billNo,
			reference: upiReference(options.billId),
		});
	} catch {
		return null;
	}
}
